import { useCallback } from 'react';
import {
  Button,
  HStack,
  SegmentControl,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { useNavigate } from '@tanstack/react-router';

import { USER_ROLE_NAME } from '~/features/auth/roles';
import { authState } from '~/features/auth/state';
import { useAuthState } from '~/features/auth/useAuthState';
import { useModal } from '~/features/modals/useModal';
import {
  type ColorScheme,
  useColorScheme,
} from '~/features/settings/colorScheme';
import { useDevToolsSetting } from '~/features/settings/devTools';
import { useMockingSetting } from '~/features/settings/useMockingSetting';
import { buildTestId } from '~/utils/buildTestId';
import { clearSession } from '~/utils/storage';

import { Modal } from '../Modal';

const THEME_OPTION: ColorScheme[] = ['light', 'dark'];

const TEST_ID = 'settings-modal' as const;

export const SettingsModal = () => {
  const { closeModal } = useModal();
  const { colorScheme, toggleColorScheme } = useColorScheme();
  const { serverMocking, toggleServerMocking } = useMockingSetting();
  const { isAuthenticated, userInfo } = useAuthState();
  const { isDevToolsEnabled, toggleDevTools } = useDevToolsSetting();
  const navigate = useNavigate();
  const userRole = userInfo?.role
    ? USER_ROLE_NAME[userInfo.role]
    : 'Unavailable';

  const onClose = useCallback(() => {
    closeModal('Settings');
  }, [closeModal]);

  return (
    <Modal
      testID={TEST_ID}
      title="Settings"
      primaryAction={{
        label: 'Close',
        onPress: onClose,
      }}
    >
      <Stack flexDirection="column" flex={1}>
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {isAuthenticated ? (
            <Stack
              flex={1}
              flexDirection="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap="$4"
            >
              <Stack>
                <HStack gap="$3">
                  <Text
                    type="$bodyBase.default"
                    marginBottom="$4"
                    textAlign="left"
                    minWidth={100}
                  >
                    Role:
                  </Text>
                  <Text
                    type="$bodyBase.medium"
                    marginBottom="$4"
                    textAlign="left"
                    testID={buildTestId({
                      testID: TEST_ID,
                      suffix: 'role',
                    })}
                  >
                    {userRole}
                  </Text>
                </HStack>
                <HStack gap="$3">
                  <Text
                    type="$bodyBase.default"
                    marginBottom="$4"
                    textAlign="left"
                    minWidth={100}
                  >
                    User ID:
                  </Text>
                  <Text
                    type="$bodyBase.medium"
                    marginBottom="$4"
                    textAlign="left"
                    testID={buildTestId({
                      testID: TEST_ID,
                      suffix: ['user', 'id'],
                    })}
                  >
                    {userInfo?.userID}
                  </Text>
                </HStack>
                <HStack gap="$3">
                  <Text
                    type="$bodyBase.default"
                    marginBottom="$4"
                    textAlign="left"
                    minWidth={100}
                  >
                    IDN ID:
                  </Text>
                  <Text
                    type="$bodyBase.medium"
                    marginBottom="$4"
                    textAlign="left"
                    testID={buildTestId({
                      testID: TEST_ID,
                      suffix: ['idn', 'id'],
                    })}
                  >
                    {userInfo?.idnId ?? 'Not assigned'}
                  </Text>
                </HStack>
                <HStack gap="$3">
                  <Text
                    type="$bodyBase.default"
                    marginBottom="$4"
                    textAlign="left"
                    minWidth={100}
                  >
                    Department ID:
                  </Text>
                  <Text
                    type="$bodyBase.medium"
                    marginBottom="$4"
                    textAlign="left"
                    testID={buildTestId({
                      testID: TEST_ID,
                      suffix: ['department', 'id'],
                    })}
                  >
                    {userInfo?.departmentId ?? 'Not assigned'}
                  </Text>
                </HStack>
                {userInfo?.departmentId && (
                  <HStack gap="$3">
                    <Text
                      type="$bodyBase.default"
                      marginBottom="$4"
                      textAlign="left"
                      minWidth={100}
                    >
                      Department Name:
                    </Text>
                    <Text
                      type="$bodyBase.medium"
                      marginBottom="$4"
                      textAlign="left"
                      testID={buildTestId({
                        testID: TEST_ID,
                        suffix: ['department', 'id'],
                      })}
                    >
                      {userInfo.departmentName}
                    </Text>
                  </HStack>
                )}
                <HStack gap="$3">
                  <Text
                    type="$bodyBase.default"
                    marginBottom="$4"
                    textAlign="left"
                    minWidth={100}
                  >
                    Name:
                  </Text>
                  <Text
                    type="$bodyBase.medium"
                    marginBottom="$4"
                    textAlign="left"
                    testID={buildTestId({
                      testID: TEST_ID,
                      suffix: ['user', 'id'],
                    })}
                  >
                    {userInfo?.firstName} {userInfo?.lastName}
                  </Text>
                </HStack>

                <HStack gap="$3">
                  <Text
                    type="$bodyBase.default"
                    marginBottom="$4"
                    textAlign="left"
                    minWidth={100}
                  >
                    Email:
                  </Text>
                  <Text
                    type="$bodyBase.medium"
                    marginBottom="$4"
                    textAlign="left"
                    testID={buildTestId({
                      testID: TEST_ID,
                      suffix: ['user', 'id'],
                    })}
                  >
                    {userInfo?.email}
                  </Text>
                </HStack>
              </Stack>
              <Stack gap="$2">
                <Button
                  aria-label="Logout"
                  label="Logout"
                  onPress={() => {
                    authState.reset();
                    void navigate({ to: '/login', replace: true });
                    onClose();
                  }}
                  size="medium"
                  variant="secondary"
                  testID={buildTestId({
                    testID: TEST_ID,
                    suffix: ['logout', 'button'],
                  })}
                />
                <Button
                  aria-label="Reset Session Data"
                  label="Reset Session Data"
                  onPress={() => {
                    clearSession();
                    window.location.reload();
                  }}
                  size="medium"
                  variant="secondary"
                  testID={buildTestId({
                    testID: TEST_ID,
                    suffix: ['reset', 'session', 'button'],
                  })}
                />
              </Stack>
            </Stack>
          ) : (
            <>
              <Text
                type="$bodyBase.medium"
                marginBottom="$4"
                textAlign="center"
              >
                You are currently not logged in!
              </Text>
              <Button
                aria-label="login"
                label="Login"
                onPress={() => {
                  void navigate({ to: '/login' });
                  onClose();
                }}
                size="medium"
                variant="secondary"
                marginBottom="$4"
                testID={buildTestId({
                  testID: TEST_ID,
                  suffix: ['login', 'button'],
                })}
              />
            </>
          )}
        </Stack>

        <HStack gap="$3">
          <Text type="$bodyBase.default" textAlign="left" minWidth={100}>
            App Version:
          </Text>
          <Text
            type="$bodyBase.medium"
            textAlign="left"
            testID={buildTestId({
              testID: TEST_ID,
              suffix: ['app', 'version'],
            })}
          >
            {__APP_VERSION__}
          </Text>
        </HStack>

        <Text type="$bodyBase.default" marginVertical="$4">
          Color Theme
        </Text>

        <SegmentControl
          createAccessibilityHint={() => 'Select a category'}
          selectedIndex={THEME_OPTION.indexOf(colorScheme)}
          items={THEME_OPTION}
          onChange={toggleColorScheme}
          testID={buildTestId({
            testID: TEST_ID,
            suffix: ['color', 'theme', 'control'],
          })}
        />

        <Text type="$bodyBase.default" marginVertical="$4">
          Server Mocking
        </Text>

        <SegmentControl
          createAccessibilityHint={() => 'Select the server mocking state'}
          selectedIndex={serverMocking ? 0 : 1}
          items={['enabled', 'disabled']}
          onChange={toggleServerMocking}
          testID={buildTestId({
            testID: TEST_ID,
            suffix: ['server', 'mocking', 'control'],
          })}
        />

        {process.env.VITEST || !import.meta.env.DEV ? null : (
          <>
            <Text type="$bodyBase.default" marginVertical="$4">
              Development Tools
            </Text>

            <SegmentControl
              createAccessibilityHint={() =>
                'Select the development tools state'
              }
              selectedIndex={isDevToolsEnabled ? 0 : 1}
              items={['enabled', 'disabled']}
              onChange={toggleDevTools}
              testID={buildTestId({
                testID: TEST_ID,
                suffix: ['dev', 'tools', 'control'],
              })}
            />
          </>
        )}
      </Stack>
    </Modal>
  );
};
